import { useState } from "react";

import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";

import { PersonalisationHeading } from "./PersonalisationHeading";
import { PersonalisationProgressBar } from "./PersonalisationProgressBar";
import { PersonalisationScreenLayout } from "./PersonalisationScreenLayout";
import { SingleChoiceTagPicker } from "./SingleChoiceTagPicker";

interface JourneyOptionItem {
  id: string;
  label: string;
}

interface PickYourJourneyProps {
  options?: JourneyOptionItem[];
  onContinue: (selection: string) => void;
  onSkip?: () => void;
  selectedColor?: string;
  continueButtonText?: string;
  skipText?: string;
  progressPercentage: number;
  onBack?: () => void;
  onClose?: () => void;
  initialSelectedId?: string | null;
}

const defaultJourneyOptions: JourneyOptionItem[] = [
  { id: "beginner", label: "Just beginning my journey" },
  { id: "intermediate", label: "Some experience, exploring further" },
  { id: "advanced", label: "Well-versed, continuing my journey" },
];

export const PickYourJourney = ({
  options = defaultJourneyOptions,
  onContinue,
  onSkip,
  selectedColor,
  continueButtonText = "Continue",
  skipText = "Skip for now",
  progressPercentage,
  onBack,
  onClose,
  initialSelectedId,
}: PickYourJourneyProps) => {
  const [selected, setSelected] = useState<JourneyOptionItem | null>(
    initialSelectedId
      ? options.find((o) => o.id === initialSelectedId) ?? null
      : null
  );

  const handleSelectionChange = (item: JourneyOptionItem | null) => {
    setSelected(item);
  };

  const handleContinue = () => {
    if (!selected) {
      return;
    }
    onContinue?.(selected.id);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  const handleBack = () => {
    onBack?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <PersonalisationScreenLayout
      header={
        <PersonalisationProgressBar
          progressPercentage={progressPercentage}
          onBack={handleBack}
          onClose={handleClose}
        />
      }
      footer={
        <>
          <Button
            style="primary"
            size="large"
            fullWidth
            disabled={!selected}
            onClick={handleContinue}
          >
            {continueButtonText}
          </Button>

          <TextButton variant="secondary" onClick={handleSkip}>
            {skipText}
          </TextButton>
        </>
      }
      contentClassName="pb-6 pt-[46px]"
    >
      <PersonalisationHeading size="sm">
        Every journey is unique — where are you on yours?
      </PersonalisationHeading>

      <SingleChoiceTagPicker
        items={options}
        selectedId={selected?.id ?? null}
        onSelectionChangeRequest={handleSelectionChange}
        selectedColor={selectedColor}
        ariaLabel="Pick your journey"
      />
    </PersonalisationScreenLayout>
  );
};
